import React from "react";
import { Button, Callout, Field, Input, Textarea } from "../components/DesignSystem";
import type { AuthSession } from "../domain/types";
import { errorMessage } from "../services/errors";
import { usePlannerData } from "../state/PlannerDataContext";

function Header({ eyebrow, title, description }: { eyebrow: string; title: string; description?: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", marginBottom: "var(--space-2)" }}>
      <span className="pob-eyebrow">{eyebrow}</span>
      <h2 style={{ font: "var(--type-section)", color: "var(--text-primary)" }}>{title}</h2>
      {description && <p style={{ font: "var(--type-body-sm)", color: "var(--text-secondary)" }}>{description}</p>}
    </div>
  );
}

function passwordErrors(password: string, confirm: string): { password?: string; confirm?: string } {
  const errors: { password?: string; confirm?: string } = {};
  if (password.length < 8) errors.password = "Use at least 8 characters.";
  if (confirm !== password) errors.confirm = "Passwords do not match.";
  return errors;
}

/**
 * "Request access" (§2) -- replaces unrestricted public account creation. Submitting only ever
 * creates a PENDING_APPROVAL record; no planner entitlements exist until an Admin approves it.
 */
export function RequestAccessScreen({ onSignIn }: { onSignIn: () => void }) {
  const { service } = usePlannerData();
  const [input, setInput] = React.useState({ name: "", email: "", phone: "", country: "", weddingDate: "", message: "" });
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [submitted, setSubmitted] = React.useState(false);

  const valid = input.name.trim().length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.email);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await service.activation.submitAccessRequest({
        name: input.name,
        email: input.email,
        phone: input.phone || undefined,
        country: input.country || undefined,
        weddingDate: input.weddingDate || undefined,
        message: input.message || undefined
      });
      setSubmitted(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <Header eyebrow="Request received" title="Thanks — we'll be in touch" />
        <Callout tone="success" title="Your request is with our team">We review every request personally and will follow up by email once yours has been reviewed.</Callout>
        <Button variant="ghost" size="sm" onClick={onSignIn}>Back to sign in</Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <Header eyebrow="New to Pockets of Beige" title="Request access" description="Tell us a little about your wedding — we review every request before opening an account." />
      {error && <Callout tone="error" title="Couldn't send that">{error}</Callout>}
      <Field label="Your name"><Input value={input.name} onChange={(e) => setInput({ ...input, name: e.target.value })} /></Field>
      <Field label="Email"><Input type="email" value={input.email} onChange={(e) => setInput({ ...input, email: e.target.value })} /></Field>
      <Field label="Phone" hint="Optional"><Input value={input.phone} onChange={(e) => setInput({ ...input, phone: e.target.value })} /></Field>
      <Field label="Country" hint="Optional"><Input value={input.country} onChange={(e) => setInput({ ...input, country: e.target.value })} /></Field>
      <Field label="Wedding date" hint="Optional, if known"><Input type="date" value={input.weddingDate} onChange={(e) => setInput({ ...input, weddingDate: e.target.value })} /></Field>
      <Field label="Anything else?" hint="Optional"><Textarea rows={3} value={input.message} onChange={(e) => setInput({ ...input, message: e.target.value })} /></Field>
      <Button variant="primary" type="submit" disabled={!valid || submitting} fullWidth>{submitting ? "Sending..." : "Request access"}</Button>
      <Button variant="ghost" size="sm" onClick={onSignIn}>Back to sign in</Button>
    </form>
  );
}

type ActivationMode = "choose" | "order" | "order-password" | "token" | "token-password";

/**
 * Order activation (§1) and approved-access-request activation (§2) share this one screen: both
 * verify a one-time credential, then collect a password and call the real sign-up endpoint with
 * the verified email (never editable in between), then finalize with the matching complete-*
 * endpoint. Neither credential is ever treated as a password itself.
 */
export function ActivationScreen({ onAuthenticated, onSignIn, presetOrderNumber }: { onAuthenticated: (session: AuthSession) => void; onSignIn: () => void; presetOrderNumber?: string }) {
  const { service, setSession, refresh } = usePlannerData();
  // A personalized "<our domain>/<order number>" link (replacing the old Google Form's separate
  // username+order-number gate) drops the customer straight into the order step with the number
  // already filled in -- they only need to confirm their email/phone, one field instead of two.
  // Verification is still a real server round-trip (customer-activation.service.ts), so a link
  // alone never grants entry on its own.
  const [mode, setMode] = React.useState<ActivationMode>(presetOrderNumber ? "order" : "choose");
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  // Order sub-flow
  const [emailOrPhone, setEmailOrPhone] = React.useState("");
  const [orderNumber, setOrderNumber] = React.useState(presetOrderNumber || "");
  // email is null for a phone-only order (no email on file) -- the customer supplies their own at
  // the password step below instead, since account creation needs a real one regardless.
  const [orderInfo, setOrderInfo] = React.useState<{ email: string | null; customerName: string; orderNumber: string } | null>(null);
  const [customerEmail, setCustomerEmail] = React.useState("");

  // Token sub-flow
  const [token, setToken] = React.useState("");
  const [tokenInfo, setTokenInfo] = React.useState<{ email: string; name: string } | null>(null);

  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const pwErrors = passwordErrors(password, confirm);

  const verifyOrder = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await service.activation.verifyOrder(emailOrPhone, orderNumber);
      if (!result.valid) {
        setError("We couldn't verify that order. Double-check your email/phone and order number.");
        return;
      }
      setOrderInfo({ email: result.email ?? null, customerName: result.customerName || "", orderNumber: result.orderNumber! });
      setMode("order-password");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const verifyToken = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await service.activation.verifyAccessToken(token);
      if (!result.valid) {
        setError("That activation link is no longer valid. Check the link or contact support.");
        return;
      }
      setTokenInfo({ email: result.email!, name: result.name || "" });
      setMode("token-password");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  // Product Excellence Pass 1 §11: a signed-up-but-not-yet-activated account must never reach
  // App.tsx's routing, which treats any truthy session as "go to Onboarding" -- that's exactly
  // the failure window the audit found (a customer whose order-completion call hit a transient
  // error was silently dropped into ordinary self-serve wedding setup). `pendingSession` holds the
  // real, already-created session locally without calling `setSession` (which App.tsx reacts to)
  // until activation has actually finished; retrying re-uses that same session/account rather than
  // signing up a second time, and never mints a second, unrelated wedding.
  const [pendingSession, setPendingSession] = React.useState<AuthSession | null>(null);
  const [activationFailed, setActivationFailed] = React.useState(false);

  const finishActivation = async (kind: "order" | "token", session: AuthSession) => {
    if (kind === "order") await service.activation.completeOrderActivation(orderInfo!.orderNumber);
    else await service.activation.completeAccessRequestActivation(token);
    setSession(session);
    await refresh();
    onAuthenticated(session);
  };

  const createPasswordAndActivate = async (kind: "order" | "token") => {
    if (pwErrors.password || pwErrors.confirm) return;
    setSubmitting(true);
    setError(null);
    try {
      const email = kind === "order" ? orderInfo!.email || customerEmail : tokenInfo!.email;
      const name = kind === "order" ? orderInfo!.customerName : tokenInfo!.name;
      const session = await service.auth.signUp({ name: name || email, email, password });
      try {
        await finishActivation(kind, session);
      } catch (completionErr) {
        // The account now genuinely exists (signUp succeeded) but isn't connected to the
        // order/request yet -- hold the session locally and offer a real retry rather than
        // letting the caller's catch below treat this like signUp itself failed.
        setPendingSession(session);
        setActivationFailed(true);
        setError(errorMessage(completionErr));
        return;
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const retryActivation = async (kind: "order" | "token") => {
    if (!pendingSession) return;
    setSubmitting(true);
    setError(null);
    try {
      await finishActivation(kind, pendingSession);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (activationFailed && pendingSession) {
    const kind: "order" | "token" = orderInfo ? "order" : "token";
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <Header eyebrow="Almost there" title="Your account was created" description="We couldn't finish connecting it to your order just now -- your password is set and nothing needs to be re-entered. Try again below." />
        <Callout tone="error" title="Couldn't finish activation">{error}</Callout>
        <Button variant="primary" fullWidth disabled={submitting} onClick={() => void retryActivation(kind)}>{submitting ? "Retrying..." : "Retry activation"}</Button>
      </div>
    );
  }

  if (mode === "choose") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <Header eyebrow="Existing Pockets of Beige customer" title="Activate my Pockets of Beige account" description="Use the order number from your invitation order, or an activation link from our team." />
        <Button variant="primary" fullWidth onClick={() => setMode("order")}>I have a POB order number</Button>
        <Button fullWidth onClick={() => setMode("token")}>I have an activation link/code</Button>
        <Button variant="ghost" size="sm" onClick={onSignIn}>Back to sign in</Button>
      </div>
    );
  }

  if (mode === "order") {
    return (
      <form onSubmit={(e) => { e.preventDefault(); void verifyOrder(); }} style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <Header
          eyebrow="Step 1 of 2"
          title="Verify your order"
          description={presetOrderNumber ? "We've recognized your Pockets of Beige order — just confirm the email or phone it's under." : undefined}
        />
        {error && <Callout tone="error" title="Couldn't verify that">{error}</Callout>}
        <Field label="Email or phone on your order"><Input value={emailOrPhone} onChange={(e) => setEmailOrPhone(e.target.value)} /></Field>
        <Field label="POB order number"><Input value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} /></Field>
        <Button variant="primary" type="submit" disabled={!emailOrPhone || !orderNumber || submitting} fullWidth>{submitting ? "Checking..." : "Verify order"}</Button>
        {!presetOrderNumber && <Button variant="ghost" size="sm" onClick={() => setMode("choose")}>Back</Button>}
        {presetOrderNumber && <Button variant="ghost" size="sm" onClick={onSignIn}>Already have a password? Sign in</Button>}
      </form>
    );
  }

  if (mode === "order-password" && orderInfo) {
    const needsEmail = !orderInfo.email;
    const emailValid = !needsEmail || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail.trim());
    return (
      <form onSubmit={(e) => { e.preventDefault(); void createPasswordAndActivate("order"); }} style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <Header eyebrow="Step 2 of 2" title="Create your password" />
        <Callout tone="success" title="Order verified ✓">Order #{orderInfo.orderNumber} · {orderInfo.customerName}</Callout>
        {error && <Callout tone="error" title="Couldn't finish activation">{error}</Callout>}
        {needsEmail ? (
          <Field label="Email" hint="Your order was placed with a phone number — add an email to finish setting up your account.">
            <Input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} />
          </Field>
        ) : (
          <Field label="Email"><Input value={orderInfo.email ?? ""} readOnly /></Field>
        )}
        <Field label="Create password" error={pwErrors.password}><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} invalid={Boolean(pwErrors.password)} /></Field>
        <Field label="Confirm password" error={pwErrors.confirm}><Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} invalid={Boolean(pwErrors.confirm)} /></Field>
        <Button variant="primary" type="submit" disabled={submitting || !emailValid} fullWidth>{submitting ? "Activating..." : "Activate my account"}</Button>
      </form>
    );
  }

  if (mode === "token") {
    return (
      <form onSubmit={(e) => { e.preventDefault(); void verifyToken(); }} style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <Header eyebrow="Step 1 of 2" title="Verify your activation link" />
        {error && <Callout tone="error" title="Couldn't verify that">{error}</Callout>}
        <Field label="Activation code"><Input value={token} onChange={(e) => setToken(e.target.value)} /></Field>
        <Button variant="primary" type="submit" disabled={!token || submitting} fullWidth>{submitting ? "Checking..." : "Verify"}</Button>
        <Button variant="ghost" size="sm" onClick={() => setMode("choose")}>Back</Button>
      </form>
    );
  }

  if (mode === "token-password" && tokenInfo) {
    return (
      <form onSubmit={(e) => { e.preventDefault(); void createPasswordAndActivate("token"); }} style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <Header eyebrow="Step 2 of 2" title="Create your password" />
        <Callout tone="success" title="Access approved ✓">{tokenInfo.name}</Callout>
        {error && <Callout tone="error" title="Couldn't finish activation">{error}</Callout>}
        <Field label="Email"><Input value={tokenInfo.email} readOnly /></Field>
        <Field label="Create password" error={pwErrors.password}><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} invalid={Boolean(pwErrors.password)} /></Field>
        <Field label="Confirm password" error={pwErrors.confirm}><Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} invalid={Boolean(pwErrors.confirm)} /></Field>
        <Button variant="primary" type="submit" disabled={submitting} fullWidth>{submitting ? "Activating..." : "Activate my account"}</Button>
      </form>
    );
  }

  return null;
}
